import React, { ReactElement, useEffect, useRef, useState } from "react";
import styles from "./dropdown.module.css";
import Flyout from "../Flyout";

type Option = {
  label: string;
  value: string;
};

type DropdownProps = {
  options: Option[];
  onOptionSelect?: (option: string) => void;
  button: ReactElement;
  align?: "left" | "right" | "center";
  openOnHover?: boolean;
};

/**
 * Dropdown component used for the Shipment tables.
 * @param options - A list of options the dropdown should render.
 * @param onOptionSelect - A function that's called when an option is selected.
 * @param button - The button a user should click on to open the dropdown.
 * @param align - Determines which side of the button the dropdown menu will render.
 */
const ShipmentDropdown = ({
  options,
  onOptionSelect,
  button,
  align = "center",
  openOnHover,
}: DropdownProps) => {
  const [open, setOpen] = useState<boolean>(false);
  const ref = useRef<HTMLDivElement>(null);
  const flyoutRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const outsideOfButton = ref.current && !ref.current.contains(e.target as Node)
      const outsideOfFlyout = flyoutRef.current && !flyoutRef.current.contains(e.target as Node)
      if (outsideOfButton && outsideOfFlyout) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div
      ref={ref}
      onMouseEnter={() => {
        if (openOnHover) setOpen(true);
      }}
      onMouseLeave={() => {
        if (openOnHover) setOpen(false);
      }}
      className={`
        ${styles.dropdownContainer}
        ${align === "left" ? styles.left : ""}
        ${align === "right" ? styles.right : ""}
        ${align === "center" ? styles.center : ""}
      `}
    >
      <a
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen(true);
        }}
      >
        {button}
      </a>
      {open && (
        <Flyout className={styles.dropdownMenuContainer} matchWidth={false} anchor='right' ref={flyoutRef}>
          <ul className={styles.dropdownList}>
            {options.map(option => (
              <li
                key={option.label}
                className={styles.dropdownOption}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!!onOptionSelect) onOptionSelect(option.value);
                  setOpen(false);
                }}
              >
                {option.label}
              </li>
            ))}
          </ul>
        </Flyout>
      )}
    </div>
  );
};

export default ShipmentDropdown;